import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction
} from 'mobx';

import {
  ChannelModelType,
  CommonChannelModel,
  TgChannelModel,
  WazzupChannelModel
} from 'shared/models/channel';
import {
  ChannelKind,
  ChannelServer,
  OpenedChannelKind,
  openedChannelKindOrder,
  mapWzRevokedReasonToMessage
} from 'shared/entities/channels';
import { apiUrls, urls } from 'shared/entities/domain';
import { IChannelsStore } from 'shared/entities/store/channelsStore';
import { IRootStore } from 'shared/entities/store/rootStore';
import { LoadingStage } from 'shared/entities/meta';
import { mapErrorCodeToMessage } from 'shared/entities/common/utils';
import { localStorageHandler } from 'stores/localStorageHandler';
import { LocalStorageKey } from 'shared/entities/localStorage';
import { AppNotificationType } from 'shared/entities/appNotifications';
import { IList } from 'shared/entities/list';
import ListModel from 'shared/models/ListModel';
import { AnalyticsEvent } from 'shared/entities/analytics';
import { TranslationString } from 'shared/entities/localization';
import { OpenStateModel } from 'shared/models/openState';
import { trySendChannelAfterOnboarding } from 'stores/onboarding/analytics';

import { ComponentLoadingStore } from '../componentLoadingStore';
import { WidgetChannelModel } from '../../shared/models/channel/widget';

const CHANNEL_BANNER_ID = 'CHANNEL_BANNER_ID';
const WZ_MIGRATION_BANNER_ID = 'WZ_MIGRATION_BANNER_ID';

export default class ChannelsStore
  extends ComponentLoadingStore
  implements IChannelsStore
{
  rootStore: IRootStore;

  channels: IList<ChannelModelType> = new ListModel();

  selectedChannelId: string | null = null;

  checkingWidgetChannelId: string | null = null;

  readonly channelsModalOpened: OpenStateModel = new OpenStateModel();
  private readonly channelCreatedSubscribers = new Set<
    (channel: ChannelModelType) => void
  >();
  private abortController: AbortController | null = null;

  constructor(rootStore: IRootStore) {
    super();

    makeObservable(this, {
      channels: observable,
      selectedChannelId: observable,
      checkingWidgetChannelId: observable,
      rootStore: observable,

      unlinkedSocialNetworks: computed,
      hasChannels: computed,
      selectedChannel: computed,
      linkedState: computed,
      channelWithTokenError: computed,
      channelCountWithTokenError: computed,
      wzMigrationChannelCount: computed,
      modalOpened: computed,

      createChannel: action,
      changeSelectedChannelId: action,
      setCheckingWidgetChannelId: action,
      unselectedSelectedChannelId: action,
      remove: action.bound,
      addChannel: action
    });
    this.rootStore = rootStore;
  }

  get channelCountWithTokenError(): number {
    return this.channels.items.reduce(
      (acc, channel) => (channel.hasTokenErrors ? acc + 1 : acc),
      0
    );
  }

  /** Сколько каналов Wazzup на ключе могут перейти на подключение без ключа; 0 — баннер не нужен */
  get wzMigrationChannelCount(): number {
    if (
      !this.rootStore.projectStore.withWzOAuthFeature ||
      !this.rootStore.permissionsStore.canEditProject
    ) {
      return 0;
    }

    return this.channels.items.reduce(
      (acc, channel) =>
        channel.kind === ChannelKind.WZ && channel.canMigrateToOAuth
          ? acc + 1
          : acc,
      0
    );
  }

  get channelWithTokenError(): ChannelModelType | null {
    return (
      this.channels.items.find((channel) => channel.hasTokenErrors) || null
    );
  }

  get loaded(): boolean {
    return this.channels.loadingStage === LoadingStage.SUCCESS;
  }

  get canBeLoaded(): boolean {
    return this.rootStore.initialized;
  }

  get modalOpened(): boolean {
    return this.channelsModalOpened.opened;
  }

  openModal = (): void => {
    this.channelsModalOpened.open();
  };

  closeModal = (): void => {
    this.channelsModalOpened.close();
  };

  areAllChannelsSelected = (selectedChannelIds: string[] | null): boolean => {
    if (selectedChannelIds === null) {
      return true;
    }

    return (
      this.channels.keys.length > 0 &&
      this.channels.keys.every((channelId) =>
        selectedChannelIds.includes(channelId)
      )
    );
  };

  initReactions = (): void => {
    super.initReactions();

    this.addReaction({
      key: 'channels',
      reaction: reaction(
        () => this.unlinkedSocialNetworks,
        this.handleOpenBanner
      )
    });

    this.addReaction({
      key: 'channelCountWithTokenError',
      reaction: reaction(
        () => this.channelCountWithTokenError,
        this.handleOpenBanner
      )
    });

    this.addReaction({
      key: 'channelWithTokenError',
      reaction: reaction(
        () => this.channelWithTokenError,
        this.handleOpenBanner
      )
    });

    this.addReaction({
      key: 'wzMigrationChannelCount',
      reaction: reaction(
        () => this.wzMigrationChannelCount,
        this.handleWzMigrationBanner
      )
    });

    this.addReaction({
      key: 'channelsLoadingStage',
      reaction: reaction(
        () => this.channels.loadingStage,
        () => {
          if (this.channels.loadingStage === LoadingStage.SUCCESS) {
            this.handleOpenBanner();
          }
        }
      )
    });
  };

  get linkedState(): Record<ChannelKind, boolean> {
    return this.channels.items.reduce<Record<ChannelKind, boolean>>(
      (acc, channel) => {
        if (
          !openedChannelKindOrder.includes(channel.kind as OpenedChannelKind)
        ) {
          return acc;
        }
        if (acc[channel.kind] === undefined) {
          return {
            ...acc,
            [channel.kind]: channel.linked
          };
        }

        return acc;
      },
      {} as Record<ChannelKind, boolean>
    );
  }

  get unlinkedSocialNetworks(): number {
    return Object.values(this.linkedState).reduce(
      (acc, linked) => (!linked ? acc + 1 : acc),
      0
    );
  }

  handleCloseBanner = () => {
    localStorageHandler.setItem(LocalStorageKey.bannerWasClosed, 'true');
  };

  handleCloseWzMigrationBanner = () => {
    localStorageHandler.setItem(
      LocalStorageKey.wzMigrationBannerWasClosed,
      'true'
    );
  };

  /** Жёлтый баннер: каналы Wazzup на ключе можно перевести; исчезает, когда переводить нечего */
  handleWzMigrationBanner = () => {
    const { appNotificationsStore } = this.rootStore;
    const count = this.wzMigrationChannelCount;

    if (!count) {
      appNotificationsStore.closeBanner(WZ_MIGRATION_BANNER_ID);
      return;
    }

    if (
      localStorageHandler.getItem(
        LocalStorageKey.wzMigrationBannerWasClosed
      ) === 'true'
    ) {
      return;
    }

    appNotificationsStore.openBanner({
      id: WZ_MIGRATION_BANNER_ID,
      type: AppNotificationType.warning,
      title: (t) =>
        t('channelsStore.wzMigration', {
          ns: 'stores',
          count
        }),
      onClose: this.handleCloseWzMigrationBanner,
      link: {
        title: (t) =>
          t('channelsStore.goToChannels', {
            ns: 'stores'
          }),
        onClick: this.openModal
      }
    });
  };

  handleOpenBanner = () => {
    if (!this.unlinkedSocialNetworks && !this.channelCountWithTokenError) {
      this.rootStore.appNotificationsStore.closeBanner(CHANNEL_BANNER_ID);
      return;
    }

    if (
      !this.rootStore.projectId ||
      !this.rootStore.permissionsStore.canViewProject ||
      !this.rootStore.permissionsStore.canEditProject
    ) {
      return;
    }

    if (this.channelCountWithTokenError && this.channelWithTokenError) {
      const channelWithError = this.channelWithTokenError;
      let error: TranslationString;
      if (this.channelCountWithTokenError > 1) {
        error = (t) =>
          t('channelsStore.tokensError', {
            ns: 'stores',
            count: this.channelCountWithTokenError
          });
      } else if (
        channelWithError.kind === ChannelKind.WZ &&
        channelWithError.isWzOAuth
      ) {
        error = mapWzRevokedReasonToMessage(channelWithError.wzRevokedReason);
      } else {
        error = (t) =>
          t('channelsStore.tokenError', {
            ns: 'stores',
            channel: channelWithError.name
          });
      }

      this.rootStore.appNotificationsStore.openBanner({
        id: CHANNEL_BANNER_ID,
        type: AppNotificationType.error,
        title: error,
        onClose: this.handleCloseBanner,
        link: {
          title: (t) =>
            t('channelsStore.goToChannels', {
              ns: 'stores'
            }),
          onClick: this.rootStore.channelsStore.openModal
        }
      });

      return;
    }

    const bannerWasClosed = localStorageHandler.getItem(
      LocalStorageKey.bannerWasClosed
    );

    if (bannerWasClosed === 'true' || !this.channels.length) {
      return;
    }

    this.rootStore.appNotificationsStore.openBanner({
      id: CHANNEL_BANNER_ID,
      type: AppNotificationType.warning,
      title: (t) =>
        t('channelsStore.unlinkedSocialNetworks.error', {
          ns: 'stores',
          count: this.unlinkedSocialNetworks
        }),
      onClose: this.handleCloseBanner,
      link: {
        title: (t) =>
          t('channelsStore.link', {
            ns: 'stores'
          }),
        to: urls.SETTINGS.PROFILE.create()
      }
    });
  };

  get selectedChannel(): ChannelModelType | null {
    return this.selectedChannelId
      ? this.channels.entities[this.selectedChannelId]
      : null;
  }

  changeSelectedChannelId = (channelId: string | null): void => {
    this.selectedChannelId = channelId;
  };

  setCheckingWidgetChannelId = (id: string | null): void => {
    this.checkingWidgetChannelId = id;
  };

  unselectedSelectedChannelId = (): void => {
    this.changeSelectedChannelId(null);
  };

  get hasChannels(): boolean {
    return !!this.channels.items.length;
  }

  addChannel = (channel: CommonChannelModel): void => {
    this.channels.addEntity({ entity: channel, key: channel.id });
  };

  subscribeChannelCreated = (
    callback: (channel: ChannelModelType) => void
  ): (() => void) => {
    this.channelCreatedSubscribers.add(callback);

    return () => {
      this.channelCreatedSubscribers.delete(callback);
    };
  };

  remove(id: string): void {
    this.channels.removeEntity(id);
  }

  async ensureLoaded(): Promise<BaseResponse> {
    if (this.loaded) {
      return { isError: false };
    }

    return this.load();
  }

  async load(): Promise<BaseResponse> {
    if (
      !this.canBeLoaded ||
      this.channels.loadingStage === LoadingStage.LOADING
    ) {
      return { isError: true };
    }

    return this.loadChannels();
  }

  async refresh(): Promise<BaseResponse> {
    if (!this.canBeLoaded) {
      return { isError: true };
    }
    this.abortController?.abort();
    return this.loadChannels();
  }

  private loadChannels = async (): Promise<BaseResponse> => {
    const abortController = new AbortController();
    this.abortController = abortController;

    this.channels.setLoadingStage(LoadingStage.LOADING);

    const response = await this.rootStore.networkStore.api<{
      channels: Array<ChannelServer>;
    }>(apiUrls.CHANNELS_LIST, {
      config: {
        signal: abortController.signal
      }
    });

    if (this.abortController !== abortController) {
      return { isError: response.isError };
    }

    if (response.isError) {
      if (response.isCancelled) {
        return { isError: true };
      }

      runInAction(() => {
        this.channels.setLoadingStage(LoadingStage.ERROR);
      });
    } else {
      const { entities, keys } = response.data.channels.reduce(
        (acc, channel) => ({
          ...acc,
          entities: {
            ...acc.entities,
            [channel._id]: this.createNewChannel(channel)
          },
          keys: [...acc.keys, channel._id]
        }),
        {
          entities: {},
          keys: []
        }
      );

      runInAction(() => {
        this.channels.addEntities({ entities, keys, initial: true });
        this.channels.setLoadingStage(LoadingStage.SUCCESS);
      });
    }

    this.abortController = null;

    return {
      isError: response.isError
    };
  };

  addCreatedChannel(channel: ChannelModelType) {
    this.channels.addEntity({ entity: channel, key: channel.id });
    this.notifyChannelCreated(channel);
  }

  async createChannel({
    channel,
    apiUrl,
    errorsMap,
    showExpectedError = false
  }: {
    channel: Record<string, any>;
    apiUrl: string;
    errorsMap: (code: string) => TranslationString;
    showExpectedError?: boolean;
  }): Promise<BaseResponse<ChannelModelType, TranslationString | null>> {
    if (this.channels.creatingStage === LoadingStage.LOADING) {
      return {
        isError: true,
        data: null
      };
    }
    this.channels.setCreatingStage(LoadingStage.LOADING);

    const response = await this.rootStore.networkStore.api<{
      channel: ChannelServer;
    }>(apiUrl, {
      method: 'POST',
      data: channel,
      errorsMap,
      showExpectedError
    });

    if (!response.isError) {
      const newChannel = this.createNewChannel(response.data.channel);

      runInAction(() => {
        this.channels.addEntity({ entity: newChannel, key: newChannel.id });
        this.channels.setCreatingStage(LoadingStage.SUCCESS);
      });

      this.notifyChannelCreated(newChannel);

      return {
        isError: false,
        data: newChannel
      };
    } else {
      runInAction(() => {
        this.channels.setCreatingStage(LoadingStage.ERROR);
      });

      return {
        isError: true,
        data: mapErrorCodeToMessage({
          errorCode: response.data?.code || null,
          map: errorsMap
        })
      };
    }
  }

  private createNewChannel(channel: ChannelServer): ChannelModelType {
    let newChannel: ChannelModelType;

    switch (channel.kind) {
      case ChannelKind.TELEGRAM:
        newChannel = TgChannelModel.fromJson(channel, this.rootStore);
        break;

      case ChannelKind.WIDGET:
        newChannel = WidgetChannelModel.fromJson(channel, this.rootStore);
        break;

      case ChannelKind.WZ:
        newChannel = WazzupChannelModel.fromJson(channel, this.rootStore);
        break;

      default:
        newChannel = CommonChannelModel.fromJson(channel, this.rootStore);
    }

    return newChannel;
  }

  /** Уведомляет подписчиков и аналитику о создании канала, не меняя список. */
  notifyChannelCreated(channel: ChannelModelType): void {
    this.rootStore.analyticsStore.sendEvent(AnalyticsEvent.channelCreationOk, {
      created_channel_kind: channel.kind
    });
    this.channelCreatedSubscribers.forEach((callback) => callback(channel));

    /** Пробуем отправить событие о создании канала после онбординга */
    trySendChannelAfterOnboarding(
      this.rootStore.analyticsStore.sendEvent.bind(
        this.rootStore.analyticsStore
      )
    );
  }

  reset(): void {
    this.abortController?.abort();
    this.abortController = null;
    this.channels.reset();
  }
}
